import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../../helper/FormGenerator';
import { PageList, FormLayout } from '../../components/PageComponents';
import { usePageAPI } from '../../hooks/usePageAPI';
import Head from '../layouts/Head';
import LoadingDashboardContent from '../../components/LoadingDashboardContent';
import { idFormat } from '../../utils/appUtils';

const AdminProductTaxClass = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages, fetchPage, createPage, updatePage, deletePage } = usePageAPI();
  const [formData, setFormData] = useState({ name: '', taxtype: '', taxvalue: '', taxIncludeExclude: 'active', term_type: 'brand' });
  const [pageData, setPageData] = useState([]);
  const [brandData, setTaxClassData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  const formFields = [
    { id: "name", label: "Name", type: "text", required: true },
    { id: "taxtype", label: "Tax Type", type: "select", options: { GST: "GST", VAT: "VAT" }, required: true },
    { id: "taxvalue", label: "Tax Value in %", type: "number", required: true },
    { id: "taxIncludeExclude", label: "Tax Include/Exclude", type: "select", options: { include: "Include", exclude: "Exclude" }, required: true },
  ];
  const sidebar = [
  ];

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    if (pageType.isList) {
      fetchPages({
        type: 'tax_classes',
        limit: limit,
        currentPage: page,
        searchBy: searchBy,
        searchValue: searchValue,
        setLoading: setLoading,
        setProgress: setProgress
      }).then(({ data, dataCount }) => {
        setPageData({
          data,
          dataCount,
        });
      });
    }

    if (pageType.isEdit) {
      const pageId = pathname.split('/').pop();
      fetchPage({ type: 'tax_classes', pageId: pageId, setLoading: setLoading, setProgress: setProgress }).then(data => {
        setFormData({
          name: data.data.name || '',
          taxtype: data.data.taxtype || '',
          taxvalue: data.data.taxvalue || '',
          taxIncludeExclude: data.data.taxIncludeExclude || '',
        });
      });
    }
    
    if (isReload) {
      setIsReload(false);
    }
  }, [pathname, search, isReload]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Create a new FormData object and append the fields directly
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('name', formData.name);
    formDataToSubmit.append('taxtype', formData.taxtype);
    formDataToSubmit.append('taxvalue', e.target.taxvalue.value);
    formDataToSubmit.append('taxIncludeExclude', formData.taxIncludeExclude);

    // Assuming 'image' is a file input, so we append the file object
    const toastId = toast.loading(`Page ${pageType.isEdit ? 'updating ...' : 'creating ...'}`, { autoClose: false });

    try {
      const pageId = pageType.isEdit ? pathname.split('/').pop() : null;

      // Make the request to create or update the page
      const result = pageType.isEdit
        ? await updatePage({ type: 'tax_classes', pageId: pageId, formDataToSubmit: formDataToSubmit })
        : await createPage({ type: 'tax_classes', formDataToSubmit: formDataToSubmit });

      if (result) {
        toast.update(toastId, {
          render: `Tax Class ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        navigate(`/admin/product/taxclass/edit/${pageType.isEdit ? pageId : result.data.id}`);
      } else {
        toast.update(toastId, {
          render: result.error,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error?.result?.data?.error || 'An unknown error occurred';

      // Update toast with error message
      toast.update(toastId, {
        render: errorMessage,  // Show the error message returned from the server
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      // Optional: Log the error to console for debugging
      console.error('Error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      const toastId = toast.loading('Deleting ...', { autoClose: false });
      const success = await deletePage({ type: 'tax_classes', id: id, setLoading: setLoading });
      if (success) {
        setPageData(prev => ({
          ...prev,
          data: prev.data.filter(page => page.id !== id),
        }));
        toast.update(toastId, {
          render: `Page deleted successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/product/taxclass/list', search: params.toString() });
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isNew || pageType.isEdit ? (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <FormLayout
            title={pageType.isEdit ? "Edit Tax Class" : "Create New Tax Class"}
            submitText={pageType.isEdit ? (loading ? 'Updating ...' : 'Update') : (loading ? 'Creating ...' : 'Create')}
            onCancel={() => navigate('/admin/product/taxclass')}
            onSubmit={handleFormSubmit}
            loading={loading}
          >
            <FormGenerator
              fields={formFields}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            />
            <FormGenerator
              fields={sidebar}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            />
          </FormLayout>
      ) : null}

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <PageList
            itemType={'tax_classes'}
            itemId={'id'}
            pageTitle={'Tax Class List'}
            pageData={pageData.data}
            actionApplyKeyMap={{
              'Delete': 'delete',
            }}
            columns={['Name', 'Tax Type', 'Tax Value', 'Include/Exclude']}
            filterBy={false}
            columnKeyMap={{
              'id': 'id',
              'Name': (item) => ( <Link className='a_edit ms-2' to={`/admin/product/taxclass/edit/${item.id}`}>{item.name} {idFormat(item.id)}</Link> ),
              'Tax Type': 'taxtype',
              'Tax Value': 'taxvalue',
              'Include/Exclude': 'tax_include_exclude',
            }}
            onAddNew={() => navigate('/admin/product/taxclass/new')}
            onView={(id) => navigate(`/admin/product/taxclass/view/${id}`)}
            onEdit={(id) => navigate(`/admin/product/taxclass/edit/${id}`)}
            onDelete={handlePageDelete}
            totalRows={pageData.dataCount}
            updateUrlParams={updateUrlParams}
            statusChange={{'name': 'status', 'route': 'tax_classes'}}
          />
      )}
    </>
  );
};

// Helper function for page title
const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Tax Class" : isNew ? "Create New Tax Class" : "Page List";

export default AdminProductTaxClass;
